import { ReactComponent as HotMatchBadge } from '@assets/images/app/icon-hot-match-badge.svg';
import { ReactComponent as HotTopicBadge } from '@assets/images/app/icon-hot-topic-badge.svg';
import { ReactComponent as IconCheck } from '@assets/images/app/icon-check.svg';
import { VoteResult, VoteUserContent } from '@models/user-contents';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

interface VoteCardProps {
  userContentId: string;
  title: string;
  category?: string;
  vote: VoteUserContent;
  voteResult?: VoteResult;
  publishedAt?: Date;
  styles?: { container?: React.CSSProperties; title?: React.CSSProperties; animation?: React.CSSProperties };
  isVoteDisabled?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLDivElement>) => void;
}

export const VoteCard: React.FC<VoteCardProps> = (props) => {
  const { title, vote, styles, onClick } = props;
  const [voteResult, setVoteResult] = useState<VoteResult | undefined>(props.voteResult);

  useEffect(() => {
    if (props.voteResult) {
      setVoteResult(props.voteResult);
    }
  }, [props.voteResult]);

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', backgroundColor: 'white', ...styles?.container }}
      onClick={onClick}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {props.category && (
              <div
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  color: '#808387',
                  backgroundColor: '#F5F7FB',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                {props.category}
              </div>
            )}
            {voteResult?.summary?.isMatch ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  color: '#1890FF',
                  backgroundColor: '#EBF9FF',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                <HotMatchBadge />핫 매치
              </div>
            ) : voteResult?.summary?.isHot ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  color: '#FC4242',
                  backgroundColor: '#FFF0F0',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                <HotTopicBadge />핫 토픽
              </div>
            ) : null}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#9B9DA0',
              fontSize: '12px',
              fontWeight: 500,
            }}>
            {props.publishedAt &&
              (moment(props.publishedAt).year() < moment().year()
                ? moment(props.publishedAt).format('YYYY년')
                : moment(props.publishedAt).format('M월 D일'))}
            {props.publishedAt && (voteResult?.summary?.total || 0) > 0 && (
              <div style={{ width: '2px', height: '2px', backgroundColor: '#B9BBBE', borderRadius: '50%' }} />
            )}
            {(voteResult?.summary?.total || 0) > 0 && `${voteResult?.summary?.total}명 참여`}
          </div>
        </div>
        <div
          style={{
            color: '#242424',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '22px',
            textOverflow: 'ellipsis',
            ...styles?.title,
          }}>
          {title}
        </div>
      </div>
      {voteResult ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {vote.options
            .map((option) => {
              const result = voteResult.results.find((r) => r.optionId === option.id);
              return {
                option,
                count: result?.count || 0,
                isSelected: result?.isSelected || false,
              };
            })
            .sort(
              (a, b) =>
                (vote.options.find((o) => o.id === a.option.id)?.order || 0) -
                (vote.options.find((o) => o.id === b.option.id)?.order || 0),
            )
            .map(({ option, count, isSelected }) => {
              const totalCount = voteResult.summary.total;
              const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
              const isMajority = percentage > 50;
              const color = isMajority ? option.color || '#242424' : '#808387';
              const backgroundColor = isMajority ? option.backgroundColor || 'white' : '#E8EAED';

              return (
                <div key={option.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      width: '100%',
                      height: '32px',
                      backgroundColor: '#F8F9FC',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}>
                    <div
                      style={{
                        position: 'absolute',
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: backgroundColor,
                        borderRadius: '8px',
                        transition: 'width 0.3s ease',
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '0 12px',
                        zIndex: 1,
                      }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <div style={{ color: color, fontSize: '14px', fontWeight: 700, lineHeight: '20px' }}>
                          {option.text}
                        </div>
                        {isSelected && <IconCheck color={color} />}
                      </div>
                      <div style={{ color: color, fontWeight: 700, lineHeight: '20px' }}>{percentage}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          {vote.options.map((option) => (
            <button
              key={option.id}
              className="link-button"
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '68px',
                padding: '10px',
                color: option.color || '#242424',
                backgroundColor: option.backgroundColor || 'white',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 700,
              }}>
              {option.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
